import React, { useState, useEffect } from 'react';
import { getAllPackages, PACKAGE_TYPES } from '../services/packageService';
import { db } from '../services/firebase';
import { doc, getDoc, updateDoc, Timestamp, increment } from 'firebase/firestore';
import { initiateRazorpayPayment, calculateGST, formatAmount } from '../services/razorpayService';
import { createPaymentRecord, markPaymentSuccess, markPaymentFailed } from '../services/paymentService';
import { createInvoice } from '../services/invoiceService';
import LoadingSpinner from './LoadingSpinner';

const FranchisePackagePurchase = ({ user, userData, onPurchaseSuccess }) => {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPackage, setCurrentPackage] = useState(null);
    const [processingId, setProcessingId] = useState(null);
    const [hasUsedFree, setHasUsedFree] = useState(false);

    useEffect(() => {
        fetchPackages();
        fetchCurrentSubscription();
    }, [user]);

    const fetchPackages = async () => {
        try {
            const result = await getAllPackages();
            if (result.success) {
                // Filter for franchise packages only
                const franchisePackages = result.packages.filter(pkg =>
                    pkg.type === PACKAGE_TYPES.FRANCHISE && pkg.status === 'active'
                );
                setPackages(franchisePackages);
            }
        } catch (error) {
            console.error("Error fetching packages:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCurrentSubscription = async () => {
        if (!user?.uid) return;
        try {
            const franchiseDoc = await getDoc(doc(db, 'franchises', user.uid));
            if (franchiseDoc.exists()) {
                const data = franchiseDoc.data();
                if (data.currentPackage) {
                    setCurrentPackage(data.currentPackage);
                }
                if (data.hasClaimedFreePackage) {
                    setHasUsedFree(true);
                }
            }
        } catch (error) {
            console.error("Error fetching subscription:", error);
        }
    };

    const handlePurchase = async (pkg) => {
        if (!user || !user.uid) {
            alert("Please log in to purchase a package.");
            return;
        }

        setProcessingId(pkg.id);

        try {
            // Check for free package
            if (pkg.price === 0) {
                // Direct update logic for free package
                const franchiseRef = doc(db, 'franchises', user.uid);
                const franchiseDoc = await getDoc(franchiseRef);
                const currentData = franchiseDoc.data();

                // Calculate new validity
                const now = new Date();
                const currentExpiry = currentData?.validityEnd ? new Date(currentData.validityEnd) : now;
                const newExpiry = new Date(Math.max(now.getTime(), currentExpiry.getTime()));
                newExpiry.setDate(newExpiry.getDate() + (pkg.duration || 365));

                // Calculate quota
                const additionalQuota = pkg.maxUsers || 0;

                await updateDoc(franchiseRef, {
                    currentPackage: {
                        id: pkg.id,
                        name: pkg.name,
                        price: 0,
                        purchasedAt: Timestamp.now()
                    },
                    status: 'active',
                    validityEnd: newExpiry.toISOString(),
                    totalQuota: increment(additionalQuota),
                    hasClaimedFreePackage: true,
                    updatedAt: Timestamp.now()
                });

                // Update UI state locally
                setHasUsedFree(true);
                setCurrentPackage({
                    id: pkg.id,
                    name: pkg.name,
                    price: 0,
                    purchasedAt: Timestamp.now()
                });

                alert(`Successfully activated ${pkg.name}!`);
                setProcessingId(null);
                if (onPurchaseSuccess) onPurchaseSuccess();
                return;
            }

            // 1. Calculate Amount with GST
            const { totalAmount, gstAmount, baseAmount } = calculateGST(pkg.price);

            // 2. Create Payment Record
            const paymentData = {
                userId: user.uid,
                userName: userData.name || userData.ownerName || 'Franchise User',
                userEmail: user.email,
                userType: 'franchise',
                packageId: pkg.id,
                packageName: pkg.name,
                amount: totalAmount,
                baseAmount: baseAmount,
                gstAmount: gstAmount,
                currency: 'INR',
                description: `Purchase of ${pkg.name}`,
                metadata: {
                    packageType: 'franchise',
                    duration: pkg.duration,
                    maxUsers: pkg.maxUsers
                }
            };

            const paymentRecord = await createPaymentRecord(paymentData);

            if (!paymentRecord.success) {
                throw new Error("Failed to create payment record");
            }

            // 3. Initiate Razorpay Payment
            await initiateRazorpayPayment(
                {
                    amount: totalAmount,
                    currency: 'INR',
                    name: 'Harshal Jobs',
                    description: `Franchise Package: ${pkg.name}`,
                    prefill: {
                        name: userData.name || userData.ownerName,
                        email: user.email,
                        contact: userData.phone || ''
                    },
                    theme: {
                        color: '#0E3A61'
                    }
                },
                // Success Callback
                async (response) => {
                    await handlePaymentSuccess(response, paymentRecord.paymentId, pkg, paymentData);
                },
                // Failure Callback
                async (error) => {
                    await markPaymentFailed(paymentRecord.paymentId, error.description);
                    alert("Payment failed: " + error.description);
                    setProcessingId(null);
                }
            );

        } catch (error) {
            console.error("Purchase error:", error);
            alert("An error occurred while initiating purchase. Please try again.");
            setProcessingId(null);
        }
    };

    const handlePaymentSuccess = async (razorpayResponse, paymentId, pkg, paymentData) => {
        try {
            // 1. Mark payment as success
            await markPaymentSuccess(paymentId, {
                razorpay_payment_id: razorpayResponse.razorpay_payment_id,
                razorpay_order_id: razorpayResponse.razorpay_order_id,
                razorpay_signature: razorpayResponse.razorpay_signature
            });

            // 2. Generate Invoice
            await createInvoice({
                userId: user.uid,
                paymentId: paymentId,
                amount: paymentData.amount,
                items: [{
                    description: `Franchise Package - ${pkg.name}`,
                    amount: paymentData.amount
                }],
                billingDetails: {
                    name: userData.name || userData.ownerName,
                    address: userData.address || "Not Provided",
                    email: user.email,
                    gstin: userData.gstin
                }
            });

            // 3. Update Franchise Data (Quota & Validity)
            const franchiseRef = doc(db, 'franchises', user.uid);
            const franchiseDoc = await getDoc(franchiseRef);
            const currentData = franchiseDoc.data();

            // Calculate new validity
            const now = new Date();
            const currentExpiry = currentData?.validityEnd ? new Date(currentData.validityEnd) : now;
            const newExpiry = new Date(Math.max(now.getTime(), currentExpiry.getTime()));
            newExpiry.setDate(newExpiry.getDate() + (pkg.duration || 365));

            // Calculate quota
            const additionalQuota = pkg.maxUsers || 0;

            await updateDoc(franchiseRef, {
                currentPackage: {
                    id: pkg.id,
                    name: pkg.name,
                    price: pkg.price,
                    purchasedAt: Timestamp.now()
                },
                status: 'active',
                validityEnd: newExpiry.toISOString(),
                totalQuota: increment(additionalQuota),
                updatedAt: Timestamp.now()
            });

            // 4. Update UI state locally
            setCurrentPackage({
                id: pkg.id,
                name: pkg.name,
                price: pkg.price,
                purchasedAt: Timestamp.now()
            });

            alert(`Successfully purchased ${pkg.name}! Your account has been updated.`);
            if (onPurchaseSuccess) onPurchaseSuccess();

        } catch (error) {
            console.error("Post-payment processing error:", error);
            alert("Payment successful but failed to update subscription. Please contact support.");
        } finally {
            setProcessingId(null);
        }
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            
            {/* Header section */}
            <div className="text-center mb-12">
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-black text-[#0E3A61] tracking-tight mb-3">
                    Partnership Allocations
                </h1>
                <p className="text-slate-600 max-w-2xl mx-auto text-sm sm:text-base font-medium px-2">
                    Expand regional talent acquisition networks with customized operational plans. Choose the allocation tier that fits your growth.
                </p>
            </div>

            {packages.length === 0 ? (
                <div className="text-center py-16 bg-white border border-slate-200/80 rounded-3xl p-8 max-w-md mx-auto">
                    <div className="text-slate-300 text-5xl mb-3">📦</div>
                    <h3 className="text-lg font-black text-[#0E3A61] mb-1">No Packages Available</h3>
                    <p className="text-slate-500 text-sm font-medium">Please check back later or contact partner support.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {packages.map((pkg) => {
                        const isPopular = pkg.isPopular;
                        const isCurrent = currentPackage?.id === pkg.id;
                        const { totalAmount, gstAmount, baseAmount } = calculateGST(pkg.price);

                        return (
                            <div
                                key={pkg.id}
                                className={`relative bg-white rounded-3xl rounded-tr-[3.5rem] rounded-bl-[3.5rem] shadow-md border-2 transition-all duration-300 hover:shadow-xl hover:scale-[1.03] flex flex-col justify-between ${
                                    isCurrent 
                                        ? 'border-[#508D0C] shadow-[#508D0C]/10 ring-4 ring-[#508D0C]/5' 
                                        : isPopular 
                                            ? 'border-[#0E3A61] shadow-[#0E3A61]/10 ring-4 ring-[#0E3A61]/5' 
                                            : 'border-slate-200/80 hover:border-slate-300'
                                }`}
                            >
                                {/* Card Badges */}
                                {isPopular && (
                                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                                        <span className="bg-gradient-to-r from-[#0E3A61] to-[#E2822F] text-white px-5 py-1.5 rounded-full text-xs font-black tracking-wide shadow-md">
                                            EXECUTIVE PARTNER
                                        </span>
                                    </div>
                                )}

                                {isCurrent && (
                                    <div className="absolute -top-4 right-6 z-10">
                                        <span className="bg-[#508D0C] text-white px-3 py-1 rounded-full text-[10px] font-black tracking-wider shadow-sm">
                                            ✓ ACTIVE PARTNERSHIP
                                        </span>
                                    </div>
                                )}

                                <div className="p-6 sm:p-8 flex-1 flex flex-col justify-between">
                                    
                                    <div>
                                        <h3 className="text-xl font-black text-slate-800 mb-4 text-center">{pkg.name}</h3>
                                        
                                        {/* Price block */}
                                        {pkg.price > 0 ? (
                                            <div className="bg-gradient-to-br from-slate-50 to-blue-50/20 border border-slate-100 rounded-2xl p-4 mb-2 text-center">
                                                <div className="flex justify-between items-center text-xs font-semibold text-slate-500 mb-1.5">
                                                    <span>Net Rate:</span>
                                                    <span className="font-bold text-slate-700">{formatAmount(pkg.price)}</span>
                                                </div>
                                                <div className="flex justify-between items-center text-xs font-semibold text-slate-500 mb-1.5">
                                                    <span>GST (18%):</span>
                                                    <span className="font-bold text-slate-700">{formatAmount(gstAmount)}</span>
                                                </div>
                                                <div className="border-t border-slate-200/60 pt-2.5 mt-2">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-xs font-bold text-[#0E3A61] uppercase tracking-wider">Total Amount:</span>
                                                        <span className="text-2xl font-black bg-gradient-to-r from-[#0E3A61] to-[#E2822F] bg-clip-text text-transparent">
                                                            {formatAmount(totalAmount)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100 rounded-2xl p-5 mb-2 text-center">
                                                <span className="text-2xl font-black text-[#508D0C] block mb-0.5">COMPLIMENTARY</span>
                                                <span className="text-xs text-emerald-800 font-bold uppercase tracking-wider">Zero Payment Required</span>
                                            </div>
                                        )}

                                        <p className="text-slate-400 text-xs font-bold uppercase tracking-wider text-center mb-6">
                                            Term of Validity: {pkg.duration} Days
                                        </p>

                                        {pkg.description && (
                                            <p className="text-xs text-slate-500 font-medium text-center mb-6 leading-relaxed min-h-[40px]">
                                                {pkg.description}
                                            </p>
                                        )}

                                        {/* Capabilities */}
                                        <div className="space-y-3.5 mb-6">
                                            {pkg.maxUsers > 0 && (
                                                <div className="flex items-center text-slate-700 bg-slate-50/80 px-4 py-2 rounded-xl border border-slate-100">
                                                    <svg className="w-5 h-5 text-[#508D0C] mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                                    </svg>
                                                    <span className="text-xs font-bold text-slate-700">{pkg.maxUsers} Employer Account Allocations</span>
                                                </div>
                                            )}

                                            {pkg.features && pkg.features.map((feature, idx) => (
                                                <div key={idx} className="flex items-center text-slate-700 bg-slate-50/50 px-4 py-2 rounded-xl border border-slate-100/50">
                                                    <svg className="w-4 h-4 text-[#508D0C] mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    <span className="text-xs font-semibold text-slate-600">{feature}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Action button */}
                                    <div>
                                        <button
                                            onClick={() => handlePurchase(pkg)}
                                            disabled={processingId === pkg.id || isCurrent || (pkg.price === 0 && hasUsedFree)}
                                            className={`w-full py-3.5 px-6 rounded-xl font-bold text-sm uppercase tracking-wider transition-all duration-300 transform active:scale-95 shadow-md hover:shadow-lg ${
                                                isCurrent
                                                    ? 'bg-emerald-50 border border-emerald-200 text-[#508D0C] cursor-default shadow-none hover:shadow-none transform-none'
                                                    : (pkg.price === 0 && hasUsedFree)
                                                        ? 'bg-slate-300 text-slate-500 cursor-not-allowed shadow-none hover:shadow-none transform-none'
                                                        : processingId === pkg.id
                                                            ? 'bg-slate-400 text-white cursor-wait shadow-none'
                                                            : isPopular
                                                                ? 'bg-gradient-to-r from-[#0E3A61] to-[#E2822F] text-white hover:opacity-95'
                                                                : 'bg-slate-800 text-white hover:bg-slate-700'
                                            }`}
                                        >
                                            {processingId === pkg.id
                                                ? 'Processing...'
                                                : isCurrent
                                                    ? 'Active Partnership'
                                                    : pkg.price === 0
                                                        ? hasUsedFree
                                                            ? 'Already Claimed'
                                                            : 'Claim Free Trial'
                                                        : 'Select Partnership Plan'}
                                        </button>
                                    </div>

                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default FranchisePackagePurchase;
